import type { ChangeEvent, FC } from 'react'
import { useEffect, useId, useMemo, useState } from 'react'

import { Button, Input, Select } from 'antd'

import { Label } from 'common/components/Label/Label'

import type { SeriesListFilter, SeriesStatus } from '../../api/series.types'
import { MAX_SEARCH } from '../../series.constants'
import { SERIES_PAGES_TEXT } from '../shared/seriesPagesText'
import styles from './seriesListFilters.module.scss'

const TEXT = SERIES_PAGES_TEXT.list.filters

type ProviderOption = { id: number; name: string }

type Props = {
  filter: SeriesListFilter
  onChange: (patch: Partial<SeriesListFilter>) => void
  onReset: () => void
  providers: ProviderOption[]
  providersState: 'loading' | 'error' | 'ready'
}

const STATUS_OPTIONS: { value: SeriesStatus | 'all'; label: string }[] = [
  { value: 'active', label: TEXT.statusActive },
  { value: 'completed', label: TEXT.statusCompleted },
  { value: 'cancelled', label: TEXT.statusCancelled },
  { value: 'all', label: TEXT.statusAll },
]

const ALL_PROVIDERS = 0

export const SeriesListFilters: FC<Props> = ({
  filter,
  onChange,
  onReset,
  providers,
  providersState,
}) => {
  const ids = useId()
  const [draft, setDraft] = useState(filter.search)

  useEffect(() => setDraft(filter.search), [filter.search])

  useEffect(() => {
    if (providersState !== 'ready' || filter.doctorId === null) return
    if (!providers.some((p) => p.id === filter.doctorId)) onChange({ doctorId: null })
  }, [providersState, providers, filter.doctorId, onChange])

  const providerOptions = useMemo(
    () => [
      { value: ALL_PROVIDERS, label: TEXT.allProviders },
      ...(providersState === 'error' ? [] : providers.map((p) => ({ value: p.id, label: p.name }))),
    ],
    [providers, providersState],
  )

  const providerValue =
    providersState === 'ready' && filter.doctorId !== null ? filter.doctorId : ALL_PROVIDERS

  const submit = () => onChange({ search: draft.trim() })

  return (
    <div className={styles.bar}>
      <div className={`${styles.field} ${styles.provider}`}>
        <Label id={`${ids}-provider`} label={TEXT.provider} className={styles.lbl} />
        <Select
          id={`${ids}-provider`}
          className={styles.control}
          value={providerValue}
          options={providerOptions}
          disabled={providersState === 'loading'}
          onChange={(value: number) =>
            onChange({ doctorId: value === ALL_PROVIDERS ? null : value })
          }
        />
      </div>
      <div className={`${styles.field} ${styles.status}`}>
        <Label id={`${ids}-status`} label={TEXT.status} className={styles.lbl} />
        <Select
          id={`${ids}-status`}
          className={styles.control}
          value={filter.status}
          options={STATUS_OPTIONS}
          onChange={(value: SeriesListFilter['status']) => onChange({ status: value })}
        />
      </div>
      <div className={`${styles.field} ${styles.searchField}`}>
        <Label id={`${ids}-search`} label={TEXT.search} className={styles.lbl} />
        <Input
          id={`${ids}-search`}
          className={styles.input}
          value={draft}
          maxLength={MAX_SEARCH}
          placeholder={TEXT.searchPlaceholder}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setDraft(e.target.value)}
          onPressEnter={submit}
        />
      </div>
      <Button className={`${styles.button} ${styles.primary}`} onClick={submit}>
        {TEXT.searchButton}
      </Button>
      <Button className={styles.button} onClick={onReset}>
        {TEXT.reset}
      </Button>
    </div>
  )
}
